import styled from '@emotion/styled';
import React, { useRef } from 'react';
import { useSelect } from 'react-aria';
import { useController } from 'react-hook-form';
import { useSelectState, Item, Section } from 'react-stately';

import { mergeRefs } from '../../../lib/mergeRefs';

import { theme } from '../../../theme/theme';

import { TextSmall } from '../../../components/common/text/TextSmall';
import { Hint } from '../../../components/forms/Hint';

import { DeviceSettingsGeneralIconListBoxOverlay } from './DeviceSettingsGeneralIconListBoxOverlay';
import { DeviceSettingsGeneralIconSelectPreview } from './DeviceSettingsGeneralIconSelectPreview';

function children(section) {
  return (
    <Section key={section.key} title={section.label} items={section.children}>
      {(option) => {
        return <Item key={option.key} textValue={option.textValue} children={option} />;
      }}
    </Section>
  );
}

export function DeviceSettingsGeneralIconSelect(props) {
  const buttonRef = useRef();

  const { field } = useController({
    name: props.name,
    control: props.control,
    rules: { required: props.required },
    defaultValue: props.defaultValue,
  });

  const selectState = useSelectState({
    items: props.options,
    children: props.children ?? children,
    defaultSelectedKey: props.defaultValue,
    onSelectionChange(value) {
      field.onChange?.(value);
      props.onChange?.(value);
    },
  });

  const select = useSelect(
    {
      label: props.label,
      'aria-label': props['aria-label'],
    },
    selectState,
    buttonRef,
  );

  const ref = mergeRefs([buttonRef, field.ref]);

  return (
    <S.Root className={props.className}>
      <S.Header color={theme.color.text_light} lineHeight="24px">
        {props.label && (
          <S.Label {...select.labelProps} title={props.label}>
            {props.renderLabel?.(props.label) ?? props.label}
          </S.Label>
        )}

        <input type="hidden" name={props.name} value={selectState.selectedKey} />

        {props.hint && <Hint hint={props.hint} />}
      </S.Header>

      <S.PreviewWrapper>
        <DeviceSettingsGeneralIconSelectPreview
          previewRef={ref}
          select={select}
          state={selectState}
          onBlur={() => field.onBlur?.()}
        />
      </S.PreviewWrapper>

      {selectState.isOpen && (
        <DeviceSettingsGeneralIconListBoxOverlay
          menuProps={select.menuProps}
          selectState={selectState}
          targetRef={props.overlayTargetRef}
          overlayStyle={props.overlayStyle}
        />
      )}
    </S.Root>
  );
}

function S() {}
DeviceSettingsGeneralIconSelect.S = S;

S.Root = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

S.Header = styled(TextSmall)`
  margin-bottom: 5px;

  /* @emotion-disable-server-rendering-unsafe-selector-warning-please-do-not-use-this-the-warning-exists-for-a-reason */
  & :not(:first-child) {
    margin-inline-start: 5px;
  }
`;

S.Label = styled.label`
  display: inline;
  min-width: 0;
  margin-bottom: 0;
`;

S.PreviewWrapper = styled.div`
  width: 100%;
`;
